// ClientDashboardPage.js (v5.3 - Stable Stripe Fix, Frontend Only)

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { ShoppingCart, CreditCard, Trash2 } from 'lucide-react';

//const API_BASE = 'http://localhost:8086';

const API_BASE = 'https://apiclientportal.myidealtd.co.uk';

export default function ClientDashboardPage() {

  const { cart, addToCart, removeFromCart } = useCart();
  const safeCart = Array.isArray(cart) ? cart : [];

  const [products, setProducts] = useState([]);
  const [userInfo, setUserInfo] = useState(null);

  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState([]);

  const [columns, setColumns] = useState(3);

  // PAYMENT STATE
  const [paymentMode, setPaymentMode] = useState(null); // full | deposit
  const [depositAmount, setDepositAmount] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const fs = (mobile, desktop) => (isMobile ? mobile : desktop);

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 768);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  // =========================
  // RESPONSIVE GRID
  // =========================
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 768) setColumns(1);
      else if (w < 1100) setColumns(2);
      else setColumns(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // =========================
  // USER
  // =========================
  useEffect(() => {
    axios.get(`${API_BASE}/api/users/profile`, { withCredentials: true })
      .then(res => setUserInfo(res.data))
      .catch(() => setUserInfo(null));
  }, []);

  // =========================
  // PRODUCTS
  // =========================
  useEffect(() => {
    axios.get(`${API_BASE}/api/products`)
      .then(res => setProducts(res.data?.products || []))
      .catch(console.error);
  }, []);

  // =========================
  // PAYMENT HISTORY
  // =========================
  useEffect(() => {
    const fetchPayments = async () => {
      if (!userInfo?._id) return;

      setPaymentsLoading(true);
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/payments/my-payments/${userInfo._id}`,
          { withCredentials: true }
        );
        setPayments(Array.isArray(data) ? data : []);
      } catch {
        setPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    };

    fetchPayments();
  }, [userInfo?._id]);

  useEffect(() => {

  const fetchPaymentSummary = async () => {

    if (!userInfo?._id) return;

    try {

      const { data } = await axios.get(
        `${API_BASE}/api/payments/my-payments-summary/${userInfo._id}`,
        { withCredentials: true }
      );

      setPaymentSummary(data);

    } catch (error) {

      console.error('Summary fetch failed:', error);

      setPaymentSummary([]);
    }
  };

  fetchPaymentSummary();

}, [userInfo?._id]);

  // =========================
  // TOTAL
  // =========================
  const total = useMemo(() => {
    return safeCart.reduce(
      (sum, i) => sum + Number(i.price || 0) * Number(i.quantity || 1),
      0
    );
  }, [safeCart]);

  // =========================
  // SAFE DEPOSIT VALIDATION
  // =========================
  const depositValue = useMemo(() => {
    const val = Number(depositAmount);

    if (!val || val <= 0) return 0;
    if (val > total) return total;

    return val;
  }, [depositAmount, total]);

  const remainingBalance = useMemo(() => {
    if (paymentMode !== 'deposit') return 0;
    return Math.max(total - depositValue, 0);
  }, [paymentMode, depositValue, total]);

  const finalAmount = useMemo(() => {
    return paymentMode === 'deposit' ? depositValue : total;
  }, [paymentMode, depositValue, total]);

  // =========================
  // STRIPE CHECKOUT (FIXED)
  // =========================
  const handleStripeCheckout = async () => {
    try {

      if (!safeCart.length) return alert('Cart is empty');
      if (!paymentMode) return alert('Select payment option');

      if (paymentMode === 'deposit' && depositValue <= 0) {
        return alert('Enter valid deposit amount');
      }

      setLoading(true);

      const formattedCart = safeCart.map(i => ({
        productId: i._id,
        name: i.name,
        price: Number(i.price),
        quantity: Number(i.quantity || 1),
      }));

      // 🔥 IMPORTANT FIX:
      // SEND POUNDS ONLY (backend handles conversion)
      const amountToSend = Number(finalAmount.toFixed(2));

      const order = await axios.post(
        `${API_BASE}/api/orders/save-order`,
        {
          products: formattedCart,
          total: amountToSend,
          paymentMode,
          depositAmount: paymentMode === 'deposit' ? depositValue : null,
          remainingBalance: paymentMode === 'deposit' ? remainingBalance : 0
        },
        { withCredentials: true }
      );

      const orderId = order.data?._id;
    
      const res = await axios.post(
      `${API_BASE}/api/stripe/create-checkout-session`,
      {
        orderId: String(orderId),

        cart: formattedCart,

        paymentMode: paymentMode,

        depositAmount:
          paymentMode === "deposit"
            ? Number(finalAmount)
            : 0,

        remainingBalance:
          paymentMode === "deposit"
            ? Number(total) - Number(finalAmount)
            : 0,

        user: {
          id: String(userInfo._id),
          email: String(userInfo.email)
        }
      },
      {
        withCredentials: true
      }
    );

      window.location.href = res.data.url;

    } catch (e) {
      console.log("Stripe error:", e?.response?.data || e.message);
      alert(e?.response?.data?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return 'https://via.placeholder.com/300';
    if (img.startsWith('http')) return img;
    return `${API_BASE}/uploads/${img}`;
  };

/* product payments */



const productPayments = useMemo(() => {
  const grouped = {};

  payments.forEach((payment) => {
    const paidAmount = (Number(payment.amount) || 0) / 100;

    if (!Array.isArray(payment.products)) return;

    payment.products.forEach((product) => {
      const productId = product.productId || product._id || product.id;
      const name = (product.name || "").trim();

      if (!productId || !name) return;

      // create a UNIQUE KEY using BOTH id + name
      const key = `${productId}_${name}`;

      if (!grouped[key]) {
        grouped[key] = {
          productId,
          name,
          totalPaid: 0
        };
      }

      grouped[key].totalPaid += paidAmount;
    });
  });

  return Object.values(grouped);
}, [payments]);


/* product payments */


  // =========================
  // UI
  // =========================
  return (
    <div style={{
  minHeight: '100vh',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  background: `
    radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 40%),
    radial-gradient(circle at top right, rgba(168,85,247,0.12), transparent 40%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)
  `,
  padding: 32,
  color: '#0f172a',
  letterSpacing: '-0.01em'
}}>

      <div style={{ maxWidth: 1500, margin: '0 auto' }}>


<div
  style={{
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: 32,
    padding: 32,
    marginBottom: 30,
    border: '1px solid rgba(255,255,255,0.5)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.05)'
  }}
>
  <div
    style={{
      fontSize: 14,
      color: '#64748b',
      marginBottom: 8
    }}
  >
    CLIENT PORTAL
  </div>

  <h1
    style={{
     fontSize: fs('28px', '36px'),
lineHeight: 1.1,
      fontWeight: 900,
      margin: 0
    }}
  >
    Welcome back,
    <br />
    {userInfo?.name}
  </h1>

  <p
    style={{
      color: '#64748b',
      marginTop: 10
    }}
  >
    Manage products, payments and balances in one place.
  </p>
</div>


        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 24
        }}>


{/* ================= PAYMENT OVERVIEW SUMMARY WIDGET ================= */}
<div
  style={{
    background: 'rgba(255,255,255,0.75)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: 24,
    marginBottom: 24,
    border: '1px solid rgba(148,163,184,0.2)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
  }}
>
  <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>
    Payment Overview
  </h2>

  {/* ================= QUICK STATS ================= */}
  <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
    <div>
      <div style={{ fontSize: 12, color: '#64748b' }}>Total Paid</div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>
        £
        {paymentSummary.reduce(
          (sum, p) => sum + (Number(p.totalPaid) || 0),
          0
        ) / 100}
      </div>
    </div>

    <div>
      <div style={{ fontSize: 12, color: '#64748b' }}>Items Tracked</div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>
        {paymentSummary.length}
      </div>
    </div>
  </div>

  {/* ================= MINI PROGRESS LIST ================= */}
  <div>
    {paymentSummary.slice(0, 3).map((product, index) => {
      const totalPaid = (product.totalPaid || 0) / 100;
      const productPrice = product.productPrice || 0;

      const progress =
        productPrice > 0
          ? Math.min(100, (totalPaid / productPrice) * 100)
          : 0;

      return (
        <div key={index} style={{ marginBottom: 10 }}>
          {/* Product name */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              fontWeight: 600
            }}
          >
            <span>{product.productName}</span>
            <span>{progress.toFixed(0)}%</span>
          </div>

          {/* Progress bar */}
          <div
            style={{
              width: '100%',
              height: 8,
              background: '#e2e8f0',
              borderRadius: 20,
              overflow: 'hidden',
              marginTop: 4
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background:
                  progress >= 100
                    ? 'linear-gradient(90deg,#22c55e,#16a34a)'
                    : 'linear-gradient(90deg,#3b82f6,#2563eb)',
                transition: 'width 0.4s ease'
              }}
            />
          </div>
        </div>
      );
    })}
  </div>

  {/* ================= CTA ================= */}
  <div style={{ marginTop: 14 }}>
    <button
      style={{
        width: '100%',
        padding: 10,
        borderRadius: 12,
        border: 'none',
        background: '#0f172a',
        color: '#fff',
        fontWeight: 700,
        cursor: 'pointer'
      }}
    >
      View full payment breakdown →
    </button>
  </div>
</div>






          {/* ================= PRODUCTS ================= */}
          <div>

            <h2 style={{
  fontSize: fs(18, 20),
fontWeight: 800,


  letterSpacing: '-0.02em',
  marginBottom: 12
}}>
  Products
</h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: 18
            }}>
              {products.map(p => (
               <div
  key={p._id}
  style={{
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(18px)',
    borderRadius: 20,
    padding: 16,
    border: '1px solid rgba(148,163,184,0.25)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
    transition: 'all 0.25s ease',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-6px)';
    e.currentTarget.style.boxShadow = '0 18px 50px rgba(0,0,0,0.12)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0px)';
    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.06)';
  }}
>
                  <img
                    src={getImageUrl(p.images?.[0] || p.image)}
                    style={{
                        width: '100%',
                        height: 190,
                        objectFit: 'cover',
                        borderRadius: 16,
                        transition: 'transform 0.3s ease'
                      }}

                   
                  />


                

                  <h3 style={{
  fontSize: isMobile ? 13 : 14,
  fontWeight: 600,
  margin: '10px 0',
  lineHeight: 1.3,
  color: '#0f172a'
}}>
  {p.name}
</h3>

                  <button
                    onClick={() => addToCart(p)}
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 10,
                      border: 'none',
                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
boxShadow: '0 6px 16px rgba(37,99,235,0.25)',
transition: 'all 0.2s ease',
//fontWeight: 700,
fontSize: isMobile ? 13 : 14,
letterSpacing: '0.2px',
                      color: '#fff'
                    }}

onMouseEnter={(e) => {
  e.currentTarget.style.transform = 'scale(1.03)';
  e.currentTarget.style.boxShadow = '0 10px 24px rgba(37,99,235,0.35)';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.boxShadow = '0 6px 16px rgba(37,99,235,0.25)';
}}


                  >





                    Add to Cart
                  </button>






                </div>
              ))}
            </div>
          </div>

          {/* ================= RIGHT PANEL ================= */}
          <div style={{ position: 'sticky', top: 20 }}>

            {/* CART */}
            <div style={{
             background: 'rgba(255,255,255,0.75)',
backdropFilter: 'blur(20px)',
padding: 22,
borderRadius: 20,
border: '1px solid rgba(148,163,184,0.25)',
boxShadow: '0 10px 30px rgba(0,0,0,0.06)'
            }}>

              <h3 style={{
  fontSize: isMobile ? 16 : 18,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  gap: 6
}}>
  <ShoppingCart size={18} /> Cart
</h3>

              <div style={{ fontSize: 30, fontWeight: 800 }}>
                £{total.toFixed(2)}
              </div>

              {/* ================= PAYMENT OPTIONS ================= */}
              <div style={{
                marginTop: 20,
                padding: 14,
                borderRadius: 12,
                background: '#f8fafc'
              }}>

                <h4>Step 1: Payment Option</h4>

                <label>
                  <input
                    type="radio"
                    checked={paymentMode === 'full'}
                    onChange={() => setPaymentMode('full')}
                  />
                  {' '} Pay Full Amount (£{total.toFixed(2)})
                </label>

                <br />

                <label>
                  <input
                    type="radio"
                    checked={paymentMode === 'deposit'}
                    onChange={() => setPaymentMode('deposit')}
                  />
                  {' '} Deposit / Part Payment
                </label>

                {paymentMode === 'deposit' && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Enter deposit amount"
                      style={{
                        width: '100%',
                        padding: 10,
                        borderRadius: 10,
                        border: '1px solid #ccc'
                      }}
                    />

                    <div style={{ marginTop: 8 }}>
                      Remaining: <b>£{remainingBalance.toFixed(2)}</b>
                    </div>
                  </div>
                )}
              </div>

              {/* ================= STRIPE BUTTON (ONLY DEPOSIT) ================= */}
              {paymentMode === 'deposit' && (
                <button
                  onClick={handleStripeCheckout}
                  disabled={loading}
                  style={{
                    marginTop: 10,
                    width: '100%',
                    padding: 12,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg,#10b981,#22c55e)',
boxShadow: '0 10px 25px rgba(34,197,94,0.25)',
fontWeight: 800,
transition: 'all 0.2s ease',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 700
                  }}

onMouseEnter={(e) => {
  e.currentTarget.style.transform = 'scale(1.02)';
  e.currentTarget.style.boxShadow = '0 14px 35px rgba(34,197,94,0.35)';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.boxShadow = '0 10px 25px rgba(34,197,94,0.25)';
}}


                >
                  <CreditCard size={16} /> Pay Deposit (£{depositValue.toFixed(2)})
                </button>
              )}

              {/* ================= BANK TRANSFER (ONLY FULL) ================= */}
              {paymentMode === 'full' && (
                <div style={{
                  marginTop: 18,
                  padding: 18,
                  borderRadius: 16,
                  background: '#ecfdf5',
                  border: '2px solid #22c55e'
                }}>

                  <h3>🏦 Bank Transfer Payment</h3>

                  <div style={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: '#14532d',
                    marginTop: 10
                  }}>
                    Pay: £{total.toFixed(2)}
                  </div>

                  <div style={{
                    background: '#fff',
                    padding: 14,
                    borderRadius: 12,
                    marginTop: 10,
                    fontSize: isMobile ? 12 : 13
                  }}>
                    <div><b>Bank:</b> HSBC UK</div>
                    <div><b>Account:</b> Your Company Ltd</div>
                    <div><b>Sort Code:</b> 12-34-56</div>
                    <div><b>Account:</b> 12345678</div>
                    <div><b>Reference:</b> {userInfo?.email}</div>
                  </div>

                  <a
                    href={`https://your-payment-link.com/pay?amount=${total}&email=${userInfo?.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      marginTop: 12,
                      textAlign: 'center',
                      padding: 14,
                      borderRadius: 12,
                  background: 'linear-gradient(135deg,#10b981,#22c55e)',
boxShadow: '0 10px 25px rgba(34,197,94,0.25)',
fontWeight: 800,
transition: 'all 0.2s ease',
                      color: '#fff',
                      
                      textDecoration: 'none'
                    }}

onMouseEnter={(e) => {
  e.currentTarget.style.transform = 'scale(1.02)';
  e.currentTarget.style.boxShadow = '0 14px 35px rgba(34,197,94,0.35)';
}}
onMouseLeave={(e) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.boxShadow = '0 10px 25px rgba(34,197,94,0.25)';
}}

                  >



                    
                    Make Bank Transfer →
                  </a>

                </div>
              )}

            </div>

            {/* CART ITEMS */}
          {/* ================= PREMIUM CART ITEMS ================= */}
<div style={{ marginTop: 18 }}>
  <h4 style={{
    fontSize: 14,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#64748b',
    marginBottom: 10
  }}>
    Cart Items
  </h4>

  {safeCart.length === 0 ? (
    <div style={{
      padding: 16,
      borderRadius: 14,
      background: 'rgba(255,255,255,0.6)',
      border: '1px solid rgba(148,163,184,0.2)',
      color: '#64748b'
    }}>
      Your cart is empty
    </div>
  ) : (
    safeCart.map(item => (
      <div
        key={item._id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 10,
          padding: 12,
          borderRadius: 16,
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(148,163,184,0.18)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.04)',
          transition: 'all 0.25s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 14px 30px rgba(0,0,0,0.10)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0px)';
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.04)';
        }}
      >

        {/* IMAGE */}
        <img
          src={getImageUrl(item.images?.[0] || item.image)}
          alt={item.name}
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            objectFit: 'cover',
            boxShadow: '0 6px 14px rgba(0,0,0,0.08)'
          }}
        />

        {/* DETAILS */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: '#0f172a'
          }}>
            {item.name}
          </div>

          <div style={{
            fontSize: 12,
            color: '#64748b',
            marginTop: 2
          }}>
            Qty: {item.quantity || 1}
          </div>
        </div>

        {/* PRICE */}
        <div style={{
          fontWeight: 800,
          fontSize: 14,
          color: '#0f172a'
        }}>
          £{(Number(item.price || 0) * Number(item.quantity || 1)).toFixed(2)}
        </div>

        {/* REMOVE */}
        <button
          onClick={() => removeFromCart(item._id)}
          style={{
            marginLeft: 8,
            width: 34,
            height: 34,
            borderRadius: 10,
            border: 'none',
            background: 'rgba(239,68,68,0.08)',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
          }}
        >
          <Trash2 size={16} />
        </button>

      </div>
    ))
  )}
</div>
          
           {/* ================= PAYMENT HISTORY (UPGRADED WITH PROGRESS BARS) ================= */}





{/* Product Payment Tracking */}


<div
  style={{
    marginTop: 20,
    background: '#fff',
    padding: 16,
    borderRadius: 16,
    border: '1px solid #e2e8f0'
  }}
>
  <h4>Your Payments</h4>

  {paymentSummary.length === 0 ? (
    <p style={{ color: '#64748b' }}>
      No product payment data
    </p>
  ) : (
    paymentSummary.map((product, index) => {

      const totalPaid = (product.totalPaid || 0) / 100;
      const productPrice = product.productPrice || 0;

      const progress =
        productPrice > 0
          ? Math.min(100, (totalPaid / productPrice) * 100)
          : 0;

      return (
        <div
          key={index}
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 10,
            background: '#f8fafc'
          }}
        >
          {/* PRODUCT NAME */}
          <div style={{ fontWeight: 700 }}>
            {product.productName}
          </div>

          {/* PAID AMOUNT */}
          <div style={{ marginTop: 4 }}>
            Total Paid So Far:
            <b> £{totalPaid.toFixed(2)}</b>
          </div>

          {/* PROGRESS BAR */}
          <div
            style={{
              width: '100%',
              height: 10,
              background: '#e2e8f0',
              borderRadius: 20,
              overflow: 'hidden',
              marginTop: 10
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background:
                  progress >= 100
                    ? 'linear-gradient(90deg,#22c55e,#16a34a)'
                    : 'linear-gradient(90deg,#3b82f6,#2563eb)',
                transition: 'width 0.4s ease'
              }}
            />
          </div>

          {/* PROGRESS TEXT */}
          <div
            style={{
              fontSize: 12,
              marginTop: 6,
              color: '#64748b'
            }}
          >
            {productPrice > 0 ? (
              <>
                £{totalPaid.toFixed(2)} of £{productPrice.toFixed(2)} (
                {progress.toFixed(0)}%)
              </>
            ) : (
              'No product price available'
            )}
          </div>
        </div>
      );
    })
  )}
</div>


{/* Product Payment Tracking */}

{/* Product Payment Tracking */}


           

          </div>
        </div>

      </div>
    </div>
  );
}